import {getAccessibleOrInaccessibleId} from 'graphql-client/InaccessibleInfo';

/** Generic helper function to get the published flowchart version from a procedure. This supports both the
 * legacy and proxy versions, and favors the proxy version when it is available.
 *
 * @param procedure - The procedure to get the published flowchart version from.
 * @returns The published flowchart version.
 */
export function getPowAgnosticPublishedFlowchartVersionFromProcedure<
  TLegacyVersion,
  TPowVersion,
  T extends {
    __typename: 'ProcedureNode';
    procedureFlowchart: {
      __typename: 'ProcedureFlowchartNode';
      publishedFlowchartVersion?: TLegacyVersion;
    };
    publishedProcedureFlowchartConfigVersionProxy?: TPowVersion;
  },
>(
  procedure: T
): T['publishedProcedureFlowchartConfigVersionProxy'] | T['procedureFlowchart']['publishedFlowchartVersion'] {
  return (
    procedure.publishedProcedureFlowchartConfigVersionProxy ??
    procedure.procedureFlowchart.publishedFlowchartVersion
  );
}

/**
 * Extracts the published procedure flowchart version ID from a procedure.
 * Returns null if no published flowchart version is available or if it is inaccessible.
 *
 * @param procedure - The procedure to extract the published flowchart version ID from.
 * @returns The ID of the published flowchart version, or null if not available.
 */
export function getPowAgnosticPublishedFlowchartVersionId<
  TLegacyVersion extends {id: string; __typename: 'ProcedureFlowchartVersionNode'},
  TPowVersion extends {id: string; __typename: 'ProcedureFlowchartConfigVersionProxyNode'},
  T extends {
    __typename: 'ProcedureNode';
    procedureFlowchart: {
      __typename: 'ProcedureFlowchartNode';
      publishedFlowchartVersion?: TLegacyVersion | null;
    };
    publishedProcedureFlowchartConfigVersionProxy?: TPowVersion | null;
  },
>(procedure: T): string | null {
  const publishedFlowchartVersion = getPowAgnosticPublishedFlowchartVersionFromProcedure(procedure);
  if (publishedFlowchartVersion == null) {
    return null;
  }
  return getAccessibleOrInaccessibleId(publishedFlowchartVersion);
}

/** Generic helper function to get the latest flowchart version from a procedure. This supports both the
 * legacy and proxy versions, and favors the proxy version when it is available.
 *
 * @param procedure - The procedure to get the latest flowchart version from.
 * @returns The latest flowchart version.
 */
export function getPowAgnosticLatestFlowchartVersionFromProcedure<
  TLegacyVersion,
  TPowVersion,
  T extends {
    __typename: 'ProcedureNode';
    procedureFlowchart: {
      __typename: 'ProcedureFlowchartNode';
      latestFlowchartVersion: TLegacyVersion;
    };
    latestProcedureFlowchartConfigVersionProxy?: TPowVersion;
  },
>(
  procedure: T
): T['latestProcedureFlowchartConfigVersionProxy'] | T['procedureFlowchart']['latestFlowchartVersion'] {
  return (
    procedure.latestProcedureFlowchartConfigVersionProxy ??
    procedure.procedureFlowchart.latestFlowchartVersion
  );
}
